import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import LeadCaptureForm from "./LeadCaptureForm";

/**
 * Sticky modal that fires after EITHER:
 *  - 30 seconds of being on the page, OR
 *  - the user scrolls past 50% of document height.
 * Dismissed state persists in sessionStorage so it doesn't re-trigger
 * after the user closes it (or after they submit successfully).
 */
const StickyLeadModal = ({ source = "apartment_search_sticky", storageKey = "unq_sticky_lead_v1" }) => {
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined") return true;
    try { return sessionStorage.getItem(storageKey) === "1"; } catch { return false; }
  });

  const markDismissed = useCallback(() => {
    try { sessionStorage.setItem(storageKey, "1"); } catch { /* private mode no-op */ }
    setDismissed(true);
    setOpen(false);
  }, [storageKey]);

  useEffect(() => {
    if (dismissed) return undefined;

    const timer = setTimeout(() => setOpen(true), 30_000);

    const onScroll = () => {
      const scrolled = window.scrollY + window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;
      if (docHeight > 0 && scrolled / docHeight >= 0.5) setOpen(true);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, [dismissed]);

  return (
    <AnimatePresence>
      {open && !dismissed && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm px-4"
          data-testid="sticky-lead-modal"
          onClick={markDismissed}
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", damping: 22, stiffness: 260 }}
            className="relative w-full sm:max-w-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={markDismissed}
              aria-label="Close"
              className="absolute -top-3 -right-3 sm:top-2 sm:right-2 z-10 p-1.5 rounded-full bg-[#1F2A44] text-white hover:bg-[#C8A96B] transition-colors shadow-md"
              data-testid="sticky-lead-close"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="bg-white rounded-2xl overflow-hidden shadow-2xl">
              <div className="bg-[#1F2A44] text-white px-6 py-4">
                <p className="text-[10px] uppercase tracking-widest text-[#C8A96B] font-bold">
                  Free Apartment Locating Service
                </p>
                <h3 className="text-lg sm:text-xl font-bold mt-1 font-['Playfair_Display']">
                  Need help narrowing it down?
                </h3>
                <p className="text-xs text-white/70 mt-1">
                  Tell us what you need — we'll text you 3 hand-picked matches.
                </p>
              </div>
              <div className="p-4 sm:p-5">
                <LeadCaptureForm
                  source={source}
                  submitLabel="Get My 3 Matches"
                  compact
                  onSuccess={() => setTimeout(markDismissed, 2200)}
                />
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default StickyLeadModal;
